import { createFileRoute } from '@tanstack/react-router';
import { useLoan, useRecordLoanPayment } from '../../hooks/useLoans';
import { useAccounts } from '../../hooks/useAccounts';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { ApiError } from '../../lib/api';
import { LoanDetailView, type LoanDetailModel, type LoanPayInput } from '../../components/loans/LoanDetailView';

export const Route = createFileRoute('/_auth/loans/$id')({
  component: LoanDetail,
});

function LoanDetail() {
  const { id } = Route.useParams();
  const companyId = useActiveCompanyId();
  const { data: loan, isLoading, error } = useLoan(companyId, id);
  const { data: accounts = [] } = useAccounts(companyId);
  const pay = useRecordLoanPayment(companyId);

  if (!companyId) return <div className="card" style={{ textAlign: 'center', padding: 32 }}><p className="muted">No active company.</p></div>;
  if (isLoading) return <p className="muted">Loading…</p>;
  if (error || !loan) return <p className="field-error">Failed to load loan.</p>;

  const payErr =
    pay.error instanceof ApiError
      ? (pay.error.body as { error?: string } | null)?.error ?? 'Payment failed'
      : pay.error?.message;

  const model: LoanDetailModel = {
    ...loan,
    bankAccountId: loan.paymentAccountId,
    payments: loan.payments.map((p) => ({
      id: p.id,
      paymentDate: p.paymentDate,
      kind: p.kind,
      scheduledPeriodNo: p.scheduledPeriodNo,
      dueDate: p.dueDate,
      accruedTo: p.accruedTo,
      amount: p.amount,
      principalPortion: p.principalPortion,
      interestPortion: p.interestPortion,
      lateCharge: p.lateCharge,
      balanceAfter: p.balanceAfter,
      journalEntryId: p.journalEntryId,
    })),
  };

  const onPay = (input: LoanPayInput) =>
    pay.mutate({
      id,
      input: { paymentDate: input.paymentDate, kind: input.kind, amount: input.amount, paymentAccountId: input.bankAccountId, memo: input.memo },
    });

  return (
    <LoanDetailView
      side="payable"
      loan={model}
      accounts={accounts}
      onPay={onPay}
      isPending={pay.isPending}
      errorMessage={payErr}
      backHref="/loans"
      backLabel="Loans"
    />
  );
}
